/**
 * Identifier-revealing variant of the code projection, for the read-only SQL
 * policy's function / package / sequence rules (`readOnlySql.ts`).
 *
 * `projectCodeOnly` masks the CONTENT of quoted identifiers (keeping the
 * quote characters) along with strings and comments — right for the keyword
 * scan (a column named `"update"` must stay harmless), wrong for the function
 * policy: `pg_catalog."setval"(…)` is the same call as `setval(…)` (release
 * review 1.5.0, round 3 N1). Only these rules read the revealed text, so
 * revealing can only ever add a refusal, never remove one.
 */

const IDENTIFIER_OPENERS = new Set(['"', '`', '[']);
const IDENTIFIER_CLOSERS = new Set(['"', '`', ']']);

/**
 * Head of the `UESCAPE 'x'` clause that follows a `U&"…"` run. Matched against
 * the COMMENT-MASKED projection, never the raw text: PostgreSQL treats a
 * comment as whitespace both before `UESCAPE` and before its literal, so
 * `U&"set!0076al" /* gap *\/ UESCAPE '!' (…)` really does call `setval` — and
 * a raw-text `\s*` accepts no comment at all, block, nested or `--` (release
 * review 1.5.0, R4 P1: it reached the adapter as read-only on preview AND on
 * untrusted interval runs). The projection blanks every comment form to spaces
 * at the same offsets, so one `\s*` covers them all.
 */
const UESCAPE_INTRO = /^\s*UESCAPE(?![\w$])\s*/i;

/** Opener of a dollar-quoted literal — mirrors `codeProjection`'s own lexer. */
const DOLLAR_TAG = /^\$([A-Za-z_][A-Za-z0-9_]*)?\$/;

/** `E'…'` escape sequences PostgreSQL decodes. `\<other>` is that character. */
const E_ESCAPE = /\\(x[0-9A-Fa-f]{1,2}|[0-7]{1,3}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8}|[\s\S])/g;
const E_NAMED: Record<string, string> = { b: '\b', f: '\f', n: '\n', r: '\r', t: '\t' };

/** A code point, or `''` when out of range — empty can only FAIL the one-character test below. */
const fromCodePoint = (digits: string, radix: number): string => {
  const value = Number.parseInt(digits, radix);
  return Number.isInteger(value) && value >= 0 && value <= 0x10ffff ? String.fromCodePoint(value) : '';
};

const decodeEString = (raw: string): string =>
  raw.replace(/''/g, "'").replace(E_ESCAPE, (_match, seq: string) => {
    const head = seq[0];
    if (head === 'x' || head === 'u' || head === 'U') return fromCodePoint(seq.slice(1), 16);
    if (head >= '0' && head <= '7') return fromCodePoint(seq, 8);
    return E_NAMED[head] ?? head;
  });

interface UescapeClause {
  /** The escape character the clause installs. */
  readonly escape: string;
  /** Index one past the clause's last delimiter character. */
  readonly end: number;
}

/**
 * The "simple string literal" PostgreSQL requires after `UESCAPE`, starting at
 * `at`. Live-verified against PostgreSQL 16 (R4): `'x'` (with `''` for a
 * literal quote), `E'x'` / `e'x'` — whose backslash escapes are decoded, so
 * `E'\x21'` and `E'\041'` are both `!` — and `$tag$x$tag$` all run; `N'x'`,
 * `B'0'`, `U&'x'` and literal concatenation are refused by the server itself
 * ("UESCAPE must be followed by a simple string literal") and so are refused
 * here. (`UESCAPE ''''` resolves to `'`, which PostgreSQL then rejects AS an
 * escape character — parsing it anyway costs nothing and blanks the clause
 * WHOLE, rather than leaving a stray `'` beside the decoded name.) The literal
 * is DELIMITED using the projection — its quote / dollar tag are the lexer's
 * own answer — and its CONTENT read back from the raw text, which is masked
 * there.
 */
function simpleStringLiteralAt(
  sql: string,
  code: string,
  at: number,
): { text: string; end: number } | null {
  const dollar = DOLLAR_TAG.exec(code.slice(at));
  if (dollar) {
    const close = code.indexOf(dollar[0], at + dollar[0].length);
    return close < 0
      ? null
      : { text: sql.slice(at + dollar[0].length, close), end: close + dollar[0].length };
  }
  const eString = code[at] === 'E' || code[at] === 'e';
  const open = eString ? at + 1 : at;
  if (code[open] !== "'") return null;
  const close = code.indexOf("'", open + 1);
  if (close < 0) return null;
  const raw = sql.slice(open + 1, close);
  return { text: eString ? decodeEString(raw) : raw.replace(/''/g, "'"), end: close + 1 };
}

/**
 * The `UESCAPE …` clause following `from` (just past the identifier's closing
 * quote), or `null` when none does. PostgreSQL requires EXACTLY one character,
 * so a literal that resolves to anything else is a server-side error and is
 * treated as no clause at all.
 */
function uescapeClauseAfter(sql: string, code: string, from: number): UescapeClause | null {
  const intro = UESCAPE_INTRO.exec(code.slice(from));
  if (!intro) return null;
  const literal = simpleStringLiteralAt(sql, code, from + intro[0].length);
  return literal && literal.text.length === 1
    ? { escape: literal.text, end: literal.end }
    : null;
}

/** Blank one revealed char, keeping newlines so the projection stays line-aligned. */
const blank = (c: string): string => (c === '\n' ? '\n' : ' ');

/**
 * PostgreSQL decodes a Unicode-escaped identifier before resolving it, so
 * `U&"set\0076al"` IS `setval` (release review 1.5.0, R2 P1 — it reached the
 * adapter as read-only). Escape forms: `\XXXX`, `\+XXXXXX`, and the escape
 * character doubled for a literal one. An unrecognised sequence is left
 * as-is rather than guessed at — the surrounding policy is a denylist, so a
 * half-decoded name simply fails to match instead of matching wrongly.
 */
const decodeUnicodeEscapes = (raw: string, escape: string): string => {
  let out = '';
  let i = 0;
  while (i < raw.length) {
    if (raw[i] !== escape) {
      out += raw[i];
      i += 1;
      continue;
    }
    if (raw[i + 1] === escape) {
      out += escape;
      i += 2;
      continue;
    }
    const wide = raw[i + 1] === '+';
    const start = i + 1 + (wide ? 1 : 0);
    const hex = raw.slice(start, start + (wide ? 6 : 4));
    if (hex.length === (wide ? 6 : 4) && /^[0-9a-f]+$/i.test(hex)) {
      const code = Number.parseInt(hex, 16);
      // Lone surrogates / out-of-range points are not identifiers PG would
      // accept; keep the raw text rather than throwing.
      out += code <= 0x10ffff ? String.fromCodePoint(code) : raw.slice(i, start + hex.length);
      i = start + hex.length;
      continue;
    }
    out += raw[i];
    i += 1;
  }
  return out;
};

/** True when the quote at `q` is the opener of a PostgreSQL `U&"…"` run. */
const opensUnicodeIdentifier = (sql: string, q: number): boolean =>
  sql[q] === '"' && q >= 2 && sql[q - 1] === '&'
  && (sql[q - 2] === 'u' || sql[q - 2] === 'U')
  && (q < 3 || !/[\w$]/.test(sql[q - 3]));

/**
 * Copies the content of every masked run the ORIGINAL text opens with an
 * identifier quote back into the projection and blanks the delimiters, so the
 * function and package patterns see `pg_catalog. setval (`. A `U&"…"` run is
 * additionally Unicode-decoded (honouring a `UESCAPE` clause, comments in it
 * included — it is blanked along with them) and
 * left-aligned in its original span — the decoded form is never longer, so
 * offsets outside the run are preserved. String literals (`'…'`, including
 * PostgreSQL's `U&'…'` Unicode STRING form) and comments stay masked: the
 * decode can never promote literal text into code.
 */
export function revealQuotedIdentifiers(sql: string, code: string): string {
  const out = code.split('');
  let i = 1;
  while (i < sql.length) {
    const opensIdentifier =
      IDENTIFIER_OPENERS.has(sql[i - 1]) && code[i - 1] === sql[i - 1]
      && code[i] === ' ' && !/\s/.test(sql[i]);
    if (!opensIdentifier) {
      i += 1;
      continue;
    }
    let end = i;
    while (end < sql.length && code[end] === ' ' && !/\s/.test(sql[end])) end += 1;
    out[i - 1] = ' ';
    for (let k = i; k < end; k += 1) {
      const c = sql[k];
      out[k] = IDENTIFIER_CLOSERS.has(c) || IDENTIFIER_OPENERS.has(c) ? ' ' : c;
    }
    const closed = end < sql.length && IDENTIFIER_CLOSERS.has(sql[end]) && code[end] === sql[end];
    if (closed) out[end] = ' ';
    const unicodeRun = opensUnicodeIdentifier(sql, i - 1);
    const uescape = unicodeRun && closed ? uescapeClauseAfter(sql, code, end + 1) : null;
    if (unicodeRun) {
      const decoded = decodeUnicodeEscapes(sql.slice(i, end), uescape ? uescape.escape : '\\');
      for (let k = i; k < end; k += 1) out[k] = decoded[k - i] ?? ' ';
      // PostgreSQL's lexer folds `UESCAPE 'x'` INTO the identifier token, so
      // `U&"upp\0065r" UESCAPE '\\' ('a')` is a call to `upper(` — blank the
      // clause, AND the comments PostgreSQL allows around it, so the decoded
      // name sits next to its parenthesis here too.
      if (uescape) for (let k = end + 1; k < uescape.end; k += 1) out[k] = blank(sql[k]);
    }
    // Past the clause, never into it: its literal must not be revealed as code.
    i = uescape ? uescape.end : end + 1;
  }
  return out.join('');
}
